import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { TradeMode } from '@shared/types/frontend/trade';
import { CovalentBalanceItem } from '@shared/types/frontend';
import { TPnLData } from '@shared/types/frontend/assetPage';
import { LiaExchangeAltSolid } from 'react-icons/lia';
import { toJpeg } from 'html-to-image';
import { IoShareSocialSharp } from 'react-icons/io5';
import confetti from 'canvas-confetti';
import { Button } from '@/components/ui/button';
import useAppDetails from '@/hooks/utilities/useAppDetails';
import TokenImage from '@/components/shared/TokenImage';
import { getNetworkLogo, getTokenImageFromAssetItem } from '@/lib/misc/tokenImageHelper';
import { toSpecialFixed } from '@/lib/misc/utils';
import useTriggerInAppReview from '@/hooks/nativeApp/useTriggerInAppReview';
interface Props {
  tradeMode: TradeMode;
  asset: CovalentBalanceItem;
  closeModal: () => void;
  showConfetti?: boolean;
  // Sell Props
  pnLData?: TPnLData;
  // Buy Props
  amount?: string;
  // Swap Props
  fromAsset?: CovalentBalanceItem;
  // Buy & Swap Props
  orderId?: number;
}
export default function SwapSuccess(p: Props) {
  const router = useRouter();
  const {
    setMobileMode
  } = useAppDetails();
  const [pnlLink, setPnlLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  const {
    ReviewModal,
    startReviewFlow
  } = useTriggerInAppReview();
  useEffect(() => {
    if (!p?.pnLData) return;
    if (p.tradeMode == TradeMode.BUY) {
      return;
    }
    if (p?.pnLData?.actualPnLChangePercent >= 30 && p?.pnLData?.realisedPNL >= 30) {
      startReviewFlow();
    }
  }, [p?.pnLData]);
  useEffect(() => {
    if (p.showConfetti) {
      confetti({
        particleCount: 250,
        spread: 60,
        zIndex: 10000
      });
    }
  }, [p.showConfetti]);
  const triggerNativeShare = (url: string) => {
    window.navigator.share({
      title: `I'm ${p.pnLData?.actualPnLChangePercent >= 0 ? 'up' : 'down'} ${toSpecialFixed(Math.abs(p.pnLData?.actualPnLChangePercent), 2).value}% on ${p.asset.contract_ticker_symbol} using @GeniusTerminal:`,
      url
    });
  };
  const handleShare = useCallback(async () => {
    if (pnlLink) {
      triggerNativeShare(pnlLink);
    } else {
      try {
        setGeneratingLink(true);
        let dataUrl = '';
        let i = 0;
        const minDataLength = 60000;
        const maxAttempts = 7;
        dataUrl = await toJpeg(imageRef.current, {
          quality: 0.9
        });
        while (i < maxAttempts) {
          dataUrl = await toJpeg(imageRef.current, {
            quality: 0.9
          });
          i += 1;
        }
        if (dataUrl.length < minDataLength) {
          throw new Error('Error creating pnl image');
        }
        const response = await fetch('/api/upload-to-s3', {
          method: 'POST',
          body: JSON.stringify({
            mod: 'pnl',
            image: dataUrl,
            metadata: {
              tokenData: p.asset,
              tokenPnLData: p.pnLData
            }
          })
        });
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }
        const result = await response.json();
        const _pnlLink = `${window?.location?.origin}${result.ulr}`;
        setPnlLink(_pnlLink);
        triggerNativeShare(_pnlLink);
      } catch (error) {
        console.error('Error generating PNL share link:', error);
      } finally {
        setGeneratingLink(false);
      }
    }
  }, [imageRef, p.asset, p.pnLData]);
  return <div className='h-full w-full flex flex-col items-center px-4 pb-6' style={{
    gap: '57px'
  }} data-sentry-component="SwapSuccess" data-sentry-source-file="SwapSuccess.tsx">
      <ReviewModal data-sentry-element="ReviewModal" data-sentry-source-file="SwapSuccess.tsx" />
      <div ref={imageRef} className='relative flex justify-center'>
        <img src='/static/trade/success-card.png' alt='Success' className='w-full h-auto' />
        <div className={`absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center gap-${p.tradeMode === TradeMode.SWAP ? 4 : 2} text-genius-cream font-medium`}>
          {p.tradeMode === TradeMode.SWAP ? <>
              <div className='flex flex-col items-center gap-3'>
                <TokenImage mainImageSrc={getTokenImageFromAssetItem(p.fromAsset)} badgeImageSrc={getNetworkLogo(p.fromAsset.network)} size={44} badgeSize={22} />
                <div className='text-[18px]'>{p.fromAsset.contract_name}</div>
              </div>
              <div className='text-[14px] mb-2 flex items-center gap-2'>
                Swapped to <LiaExchangeAltSolid className='translate-y-[1px]' />
              </div>
            </> : null}
          <div className='flex flex-col items-center gap-3'>
            <TokenImage mainImageSrc={getTokenImageFromAssetItem(p.asset)} badgeImageSrc={getNetworkLogo(p.asset.network)} size={p.tradeMode === TradeMode.SWAP ? 44 : 56} badgeSize={p.tradeMode === TradeMode.SWAP ? 22 : 28} data-sentry-element="TokenImage" data-sentry-source-file="SwapSuccess.tsx" />
            <div className={`text-[${p.tradeMode === TradeMode.SWAP ? 18 : 24}px] leading-[120%] text-center max-w-[70vw]`}>
              {p.asset.contract_name}
            </div>
          </div>
          {p.tradeMode === TradeMode.BUY ? <>
              <div className='text-[14px]'>Bought</div>
              <div className='text-[50px]'>${p.amount}</div>
            </> : p.tradeMode === TradeMode.SELL && p.pnLData?.actualPnLChangePercent ? <>
              <div className={`text-[50px] ${p.pnLData?.actualPnLChangePercent >= 0 ? 'text-[#34D399]' : 'text-red-400'}`}>
                {p.pnLData?.actualPnLChangePercent >= 0 ? '+' : ''}
                {toSpecialFixed(p.pnLData?.actualPnLChangePercent, 2).value}%
              </div>
              <div className='text-[14px]'>On</div>
              <img src='/static/genius-full-logo.png' alt='Genius' className='w-[86px] h-auto' />
            </> : null}
        </div>
      </div>
      <div className='w-full flex flex-col gap-3'>
        {p.tradeMode === TradeMode.SELL && p.pnLData?.actualPnLChangePercent ? <Button variant='pink' className='flex-1 h-12 min-h-12 rounded-xl' onClick={handleShare}>
            <div key={`${generatingLink}`} className='flex items-center gap-2 opacity-0 transition-opacity motion-safe:animate-[fadeIn_0.25s_ease-in_forwards]'>
              <IoShareSocialSharp size={18} />
              {generatingLink ? 'Generating link...' : 'Share'}
            </div>
          </Button> : <Button variant='purple' className='w-full flex-1 h-12 min-h-12 rounded-xl text-genius-cream' onClick={() => {
        setMobileMode('history');
        router.push(`/home?m_mode=history&orderId=${p.orderId}`, undefined, {
          shallow: true
        });
      }}>
            View Order
          </Button>}
        <Button variant={p.tradeMode === TradeMode.SELL ? 'purple' : 'pink'} className='flex-1 h-12 min-h-12 rounded-xl' onClick={p.closeModal} data-sentry-element="Button" data-sentry-source-file="SwapSuccess.tsx">
          Close
        </Button>
      </div>
    </div>;
}